import cn from 'classnames'
import { motion } from 'framer-motion'
import { observer } from 'mobx-react'
import React from 'react'

import { LocationValue } from '@flowwai-frontend/shared/common-components'
import { isMobile } from '@flowwai-frontend/shared/utils/web'

import { MapsFeaturesEnum } from '../../constants'
import { useMapsInstanceContext } from '../../hooks/useMapsInstanceContext'
import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../types'
import MapsZoomControlsV2 from '../MapsZoomControlsV2/MapsZoomControlsV2'
import CloseMapsTool from './components/CloseMapsTool'
import CurrentLocationTool from './components/CurrentLocationTool'
import MapStyleTool from './components/MapStyleTool'
import OpenInGoogleMapsTool from './components/OpenInGoogleMapsTool'
import PlacesSearchTool from './components/PlacesSearchTool'
import SaveLocationTool from './components/SaveLocationTool'
import SiteLocationTool from './components/SiteLocationTool'
import UndoTool from './components/UndoTool'
import ViewEditToggleTool from './components/ViewEditToggleTool'
import * as Styles from './styles'

interface Props {
   isReadOnly: boolean
   geoShape: LocationValue
   onSaveLocation: () => void
   errorMessage: string | null | undefined
   isGeoShapeChanged: boolean
   onSearchLocation: (coordinates: MapCoordinates) => void
   onUndo: () => void
   onCloseModal: () => void
   addressCoordinates?: MapCoordinates | null
}

const MapsToolbar = (props: Props): React.ReactElement => {
   const {
      geoShape,
      onSaveLocation,
      errorMessage,
      isReadOnly,
      isGeoShapeChanged,
      onUndo,

      addressCoordinates
   } = props

   const mapInstance = useMapsInstanceContext()
   const {
      defaultViewMode,
      mapsT,
      renderKMLFileUploader,
      restrictToMapStyle,
      supportedFeatures
   } = useMapsUtilityContext()

   const isMobileDevice = isMobile()

   const shouldHideLeftDivider = isReadOnly

   const renderDivider = (): React.ReactElement => (
      <div className='h-[20px] w-px bg-gray-300' />
   )

   const renderMapStyleTool = (): React.ReactElement => (
      <MapStyleTool
         selectedMapStyle={mapInstance.mapStyle}
         handleOnChangeMapPreview={mapInstance.onChangeMapStyle}
      />
   )

   const renderViewEditToggle = () => <ViewEditToggleTool />

   const renderSiteLocationTool = (): React.ReactElement => (
      <SiteLocationTool geoShape={geoShape} />
   )

   const renderCurrentLocationTool = (): React.ReactElement => {
      const shouldRenderTool = supportedFeatures?.includes(
         MapsFeaturesEnum.CurrentLocation
      )

      return shouldRenderTool ? (
         <CurrentLocationTool
            geoShape={geoShape}
            shouldNavigateOnMount={!addressCoordinates}
         />
      ) : (
         <></>
      )
   }

   const renderKMLFilesTool = (): React.ReactElement => (
      <>{renderKMLFileUploader?.()}</>
   )

   const renderOpenInGMapsTool = (): React.ReactElement => {
      const shouldRenderTool = supportedFeatures?.includes(
         MapsFeaturesEnum.OpenInGoogleMaps
      )

      return shouldRenderTool ? (
         <>
            <OpenInGoogleMapsTool geoShape={geoShape} />
            {renderDivider()}
         </>
      ) : (
         <></>
      )
   }

   const renderMapsZoomControls = (): React.ReactElement => (
      <MapsZoomControlsV2
         zoomValue={mapInstance.zoomValue}
         setZoomValue={mapInstance.onChangeZoomValue}
         t={mapsT}
      />
   )

   const renderSaveLocationTool = (): React.ReactNode => {
      if (isReadOnly) return null

      return (
         <SaveLocationTool
            onSaveLocation={onSaveLocation}
            errorMessage={errorMessage}
            isGeoShapeChanged={isGeoShapeChanged}
         />
      )
   }

   const renderUndoTool = (): React.ReactNode => (
      <UndoTool isGeoShapeChanged={isGeoShapeChanged} onUndo={onUndo} />
   )

   const renderPlacesSearchTool = (): React.ReactElement => {
      const shouldRenderTool =
         supportedFeatures?.includes(MapsFeaturesEnum.PlacesSearch) &&
         !isMobileDevice

      return shouldRenderTool ? (
         <PlacesSearchTool
            className='absolute top-6 left-6'
            onSearchLocation={props.onSearchLocation}
            animationProps={Styles.placesSearchAnimationProps}
         />
      ) : (
         <></>
      )
   }

   const renderCloseMaps = (): React.ReactElement => (
      <CloseMapsTool
         isGeoShapeChanged={isGeoShapeChanged}
         onSaveLocation={onSaveLocation}
         onCloseModal={props.onCloseModal}
         errorMessage={errorMessage}
      />
   )

   const renderDesktopTools = (): React.ReactElement => (
      <React.Fragment>
         {renderKMLFileUploader ? renderKMLFilesTool() : null}
         {renderOpenInGMapsTool()}
         {isReadOnly ? null : renderViewEditToggle()}
         {renderSaveLocationTool()}
         {renderUndoTool()}

         {shouldHideLeftDivider ? null : renderDivider()}

         {restrictToMapStyle ? null : renderMapStyleTool()}
         {renderSiteLocationTool()}
         {renderCurrentLocationTool()}
         {renderMapsZoomControls()}
      </React.Fragment>
   )

   const renderMobileTools = (): React.ReactElement => (
      <React.Fragment>
         {renderSaveLocationTool()}

         {restrictToMapStyle ? null : renderMapStyleTool()}

         {renderMapsZoomControls()}
      </React.Fragment>
   )

   return (
      <React.Fragment>
         {renderPlacesSearchTool()}
         {renderCloseMaps()}

         <motion.div
            className={cn(Styles.container)}
            {...Styles.animationProps}
         >
            {isMobileDevice ? renderMobileTools() : renderDesktopTools()}
         </motion.div>
      </React.Fragment>
   )
}

export default observer(MapsToolbar)
